import { useState } from 'react';

import { DraftError, EMPTY_DRAFT, fromDraft, toDraft, type ResponseDraft } from './draft.js';
import { ResponseFields } from './ResponseFields.jsx';
import { METHODS, type MockResponse, type Rule } from './types.js';

const INPUT =
  'rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100 ' +
  'placeholder:text-slate-600 focus:border-sky-500 focus:outline-none';

export interface RuleDraftValue {
  method: string;
  pathPattern: string;
  response: MockResponse;
}

export interface RuleFormProps {
  /** Absent when creating. */
  rule?: Rule;
  onSubmit: (value: RuleDraftValue) => Promise<void>;
  onCancel: () => void;
}

export function RuleForm({ rule, onSubmit, onCancel }: RuleFormProps) {
  const [method, setMethod] = useState(rule?.method ?? '*');
  const [pathPattern, setPathPattern] = useState(rule?.pathPattern ?? '/');
  const [draft, setDraft] = useState<ResponseDraft>(
    rule ? toDraft(rule.response) : { ...EMPTY_DRAFT },
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (): Promise<void> => {
    setError(null);

    let response: MockResponse;
    try {
      response = fromDraft(draft);
    } catch (draftError) {
      setError(draftError instanceof DraftError ? draftError.message : 'Formulario inválido.');
      return;
    }

    setSaving(true);
    try {
      await onSubmit({ method, pathPattern: pathPattern.trim(), response });
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="grid gap-4 rounded-lg border border-sky-900 bg-slate-900/80 p-4"
      data-testid="rule-form"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <div className="flex flex-wrap gap-3">
        <label className="grid gap-1">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-400">Método</span>
          <select
            className={INPUT}
            data-testid="rule-method"
            value={method}
            onChange={(event) => setMethod(event.target.value)}
          >
            {METHODS.map((verb) => (
              <option key={verb} value={verb}>
                {verb === '*' ? '* (cualquiera)' : verb}
              </option>
            ))}
          </select>
        </label>

        <label className="grid flex-1 gap-1">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-400">Ruta</span>
          <input
            className={`${INPUT} font-mono`}
            data-testid="rule-path"
            placeholder="/orders/:id/pay"
            value={pathPattern}
            onChange={(event) => setPathPattern(event.target.value)}
          />
        </label>
      </div>

      <p className="text-xs text-slate-500">
        <code>:nombre</code> captura un segmento y queda disponible como{' '}
        <code>{'{{path.nombre}}'}</code>. Un <code>*</code> final se traga el resto de la ruta.
      </p>

      <ResponseFields draft={draft} onChange={setDraft} idPrefix="rule" />

      {error ? (
        <p className="text-sm text-rose-400" role="alert" data-testid="rule-form-error">
          {error}
        </p>
      ) : null}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-50"
        >
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:border-slate-500"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
